/**
 * VÉRAULT Portal — Content Studio · Dashboard
 *
 * The publishing room overview. Surfaces what wants attention right now:
 *   · Recent drafts (4 latest, with quick continue)
 *   · Upcoming scheduled (chronological, next 4)
 *   · Recently published (last 3)
 *   · A few calm metrics + quick actions
 *
 * Phase 1 — read-only aggregate from existing /cms/journal + /cms/media endpoints.
 */
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Plus,
  ArrowRight,
  Edit3,
  Calendar,
  Images,
  Share2,
  ExternalLink,
  CircleDot,
  Send,
} from "lucide-react";
import {
  Surface,
  EditorialRule,
  StatusTag,
  Empty,
  Skeleton,
  PortalButton,
  MetricTile,
  api,
  fmt,
} from "../../../index";
import { RepurposeModal } from "./RepurposeModal";

function Section({ title, hint, action, children }) {
  return (
    <Surface variant="default" padded className="space-y-5">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h3 className="font-serif text-xl text-[hsl(var(--foreground))]">{title}</h3>
          {hint && (
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))]">
              {hint}
            </p>
          )}
        </div>
        {action}
      </div>
      {children}
    </Surface>
  );
}

function JournalRow({ entry, onContinue }) {
  return (
    <div
      className="group flex items-center justify-between gap-4 py-3 border-b border-[rgba(0,0,0,0.04)] last:border-0"
      data-testid={`content-dashboard-row-${entry.id}`}
    >
      <button
        onClick={() => onContinue(entry)}
        className="text-left min-w-0 flex-1"
      >
        <div className="font-serif text-[15px] text-[hsl(var(--foreground))] group-hover:text-[hsl(var(--accent-gold))] transition-colors truncate">
          {entry.title || "Untitled"}
        </div>
        <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] truncate">
          {[entry.category, entry.destination_tag].filter(Boolean).join(" · ") || "Uncategorized"}
        </div>
      </button>
      <div className="shrink-0 flex items-center gap-3">
        {entry.status === "scheduled" && entry.scheduled_publish_at ? (
          <span className="hidden sm:inline font-mono text-[11px] text-[hsl(var(--accent-gold))]">
            {fmt.formatDate(entry.scheduled_publish_at)}
          </span>
        ) : entry.published_at ? (
          <span className="hidden sm:inline font-mono text-[11px] text-[hsl(var(--foreground-muted))]">
            {fmt.formatDate(entry.published_at)}
          </span>
        ) : entry.updated_at ? (
          <span className="hidden sm:inline font-mono text-[11px] text-[hsl(var(--foreground-muted))]">
            Edited {fmt.formatDate(entry.updated_at)}
          </span>
        ) : null}
        <StatusTag
          tone={entry.status === "published" ? "sage" : entry.status === "scheduled" ? "gold" : "neutral"}
        >
          {entry.status || "draft"}
        </StatusTag>
      </div>
    </div>
  );
}

export default function ContentDashboard() {
  const navigate = useNavigate();
  const [entries, setEntries] = React.useState(null);
  const [mediaCount, setMediaCount] = React.useState(null);
  const [socialPosts, setSocialPosts] = React.useState([]);
  const [error, setError] = React.useState(null);
  const [repurposeEntry, setRepurposeEntry] = React.useState(null);
  const [repurposeToast, setRepurposeToast] = React.useState(null);
  const [refreshKey, setRefreshKey] = React.useState(0);

  React.useEffect(() => {
    let mounted = true;
    Promise.all([
      api.get("/cms/journal").then((r) => r.data).catch(() => null),
      api.get("/cms/media").then((r) => r.data).catch(() => null),
      api.get("/cms/social").then((r) => r.data).catch(() => []),
    ]).then(([j, m, s]) => {
      if (!mounted) return;
      if (j === null && m === null) {
        setError("Couldn't load Content Studio.");
        return;
      }
      setEntries(Array.isArray(j) ? j : []);
      setMediaCount(Array.isArray(m) ? m.length : 0);
      setSocialPosts(Array.isArray(s) ? s : []);
    });
    return () => {
      mounted = false;
    };
  }, [refreshKey]);

  const buckets = React.useMemo(() => {
    if (!entries) return null;
    const drafts = entries
      .filter((e) => e.status === "draft")
      .sort((a, b) => (b.updated_at || b.created_at || "").localeCompare(a.updated_at || a.created_at || ""))
      .slice(0, 4);
    const scheduled = entries
      .filter((e) => e.status === "scheduled" && e.scheduled_publish_at)
      .sort((a, b) => (a.scheduled_publish_at || "").localeCompare(b.scheduled_publish_at || ""))
      .slice(0, 4);
    const published = entries
      .filter((e) => e.status === "published")
      .sort((a, b) => (b.published_at || "").localeCompare(a.published_at || ""))
      .slice(0, 3);

    // counts
    const counts = {
      drafts: entries.filter((e) => e.status === "draft").length,
      scheduled: entries.filter((e) => e.status === "scheduled").length,
      published_total: entries.filter((e) => e.status === "published").length,
    };

    // published this month
    const now = new Date();
    const ym = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`;
    counts.published_this_month = entries.filter(
      (e) => e.status === "published" && (e.published_at || "").startsWith(ym),
    ).length;

    return { drafts, scheduled, published, counts };
  }, [entries]);

  // Publishing pipeline rows — top 8 journals by latest activity (updated_at,
  // published_at, or related social updated_at). Each row carries its own
  // social-post count (sourced via source_journal_id).
  const pipeline = React.useMemo(() => {
    if (!entries) return null;
    const socialByJournal = new Map();
    for (const s of socialPosts) {
      const j = (s.source_journal_id || "").trim();
      if (!j) continue;
      if (!socialByJournal.has(j)) socialByJournal.set(j, []);
      socialByJournal.get(j).push(s);
    }
    const rows = entries.map((e) => {
      const socials = socialByJournal.get(e.id) || [];
      const lastActivity =
        [e.updated_at, e.published_at, ...socials.map((s) => s.updated_at)]
          .filter(Boolean)
          .sort()
          .at(-1) || "";
      return { entry: e, socials, lastActivity };
    });
    rows.sort((a, b) => (b.lastActivity || "").localeCompare(a.lastActivity || ""));
    return rows.slice(0, 8);
  }, [entries, socialPosts]);

  const handleContinue = (entry) => {
    navigate(`/advisor/content/journal/${entry.id}`);
  };
  const handleNewEntry = () => navigate("/advisor/content/journal/new");

  if (error) {
    return <Empty title={error} icon={<Edit3 className="size-6" strokeWidth={1.25} />} />;
  }

  return (
    <div className="space-y-10" data-testid="content-dashboard">
      {/* Quick actions */}
      <div className="flex flex-wrap items-center gap-2">
        <PortalButton
          variant="primary"
          size="md"
          icon={<Plus className="size-3.5" strokeWidth={1.5} />}
          onClick={handleNewEntry}
          data-testid="content-dashboard-new-entry"
        >
          New journal entry
        </PortalButton>
        <PortalButton
          variant="ghost"
          size="md"
          icon={<Calendar className="size-3.5" strokeWidth={1.5} />}
          onClick={() => navigate("/portal/advisor/content/calendar")}
          data-testid="content-dashboard-open-calendar"
        >
          Open calendar
        </PortalButton>
        <PortalButton
          variant="ghost"
          size="md"
          icon={<Images className="size-3.5" strokeWidth={1.5} />}
          onClick={() => navigate("/advisor/content/media")}
          data-testid="content-dashboard-open-media"
        >
          Open media library
        </PortalButton>
      </div>

      {/* Metrics */}
      <div>
        <EditorialRule label="At a glance" />
        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          {buckets ? (
            <>
              <MetricTile
                label="Drafts"
                value={buckets.counts.drafts}
                format="number"
                caption="in progress"
                testid="content-metric-drafts"
              />
              <MetricTile
                label="Scheduled"
                value={buckets.counts.scheduled}
                format="number"
                caption="awaiting their moment"
                tone="accent"
                testid="content-metric-scheduled"
              />
              <MetricTile
                label="Published this month"
                value={buckets.counts.published_this_month}
                format="number"
                caption={`of ${buckets.counts.published_total} total`}
                testid="content-metric-published"
              />
              <MetricTile
                label="Media library"
                value={typeof mediaCount === "number" ? mediaCount : "—"}
                format="number"
                caption="assets on hand"
                tone="quiet"
                testid="content-metric-media"
              />
            </>
          ) : (
            [0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-[110px]" />)
          )}
        </div>
      </div>

      {/* Two-column: Drafts + Scheduled */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6">
        <Section
          title="In the workshop"
          hint="Recent drafts"
          action={
            <Link
              to="/portal/advisor/content/journal?filter=draft"
              className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))] transition-colors inline-flex items-center gap-1"
              data-testid="content-dashboard-view-drafts"
            >
              View all <ArrowRight className="size-3" strokeWidth={1.5} />
            </Link>
          }
        >
          {!buckets ? (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-14" />
              ))}
            </div>
          ) : buckets.drafts.length === 0 ? (
            <Empty
              title="No drafts in progress."
              hint="Begin a new piece — drafts are saved automatically."
              icon={<Edit3 className="size-5" strokeWidth={1.25} />}
              action={
                <PortalButton size="sm" variant="ghost" icon={<Plus className="size-3.5" />} onClick={handleNewEntry}>
                  Begin a new entry
                </PortalButton>
              }
            />
          ) : (
            <div data-testid="content-dashboard-drafts">
              {buckets.drafts.map((e) => (
                <JournalRow key={e.id} entry={e} onContinue={handleContinue} />
              ))}
            </div>
          )}
        </Section>

        <Section
          title="Coming up"
          hint="Scheduled to publish"
          action={
            <Link
              to="/portal/advisor/content/calendar"
              className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))] transition-colors inline-flex items-center gap-1"
              data-testid="content-dashboard-view-calendar"
            >
              Open calendar <ArrowRight className="size-3" strokeWidth={1.5} />
            </Link>
          }
        >
          {!buckets ? (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-14" />
              ))}
            </div>
          ) : buckets.scheduled.length === 0 ? (
            <Empty
              title="Nothing scheduled."
              hint="Set a future date on a draft to queue it up."
              icon={<Calendar className="size-5" strokeWidth={1.25} />}
            />
          ) : (
            <div data-testid="content-dashboard-scheduled">
              {buckets.scheduled.map((e) => (
                <JournalRow key={e.id} entry={e} onContinue={handleContinue} />
              ))}
            </div>
          )}
        </Section>
      </div>

      {/* Publishing pipeline — each piece's lifecycle in one row */}
      <Section
        title="The publishing pipeline"
        hint="Recent activity · Draft → Published → Repurposed"
        action={
          <Link
            to="/portal/advisor/content/journal"
            className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))] transition-colors inline-flex items-center gap-1"
            data-testid="content-dashboard-view-journal"
          >
            Open journal <ArrowRight className="size-3" strokeWidth={1.5} />
          </Link>
        }
      >
        {!pipeline ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-16" />
            ))}
          </div>
        ) : pipeline.length === 0 ? (
          <Empty
            title="Your pipeline is empty."
            hint="Begin a draft to start the rhythm."
            icon={<Send className="size-5" strokeWidth={1.25} />}
            action={
              <PortalButton size="sm" variant="ghost" icon={<Plus className="size-3.5" />} onClick={handleNewEntry}>
                Begin a new entry
              </PortalButton>
            }
          />
        ) : (
          <div className="space-y-1" data-testid="content-dashboard-pipeline">
            {pipeline.map(({ entry: e, socials, lastActivity }) => {
              const isPublished = e.status === "published";
              const isScheduled = e.status === "scheduled";
              const socialsCount = socials.length;
              return (
                <div
                  key={e.id}
                  className="group grid grid-cols-[1fr_auto] sm:grid-cols-[minmax(0,1.4fr)_minmax(0,2fr)_auto] items-center gap-3 sm:gap-5 py-3 border-b border-[rgba(0,0,0,0.04)] last:border-0"
                  data-testid={`content-dashboard-pipeline-row-${e.id}`}
                >
                  <button
                    onClick={() => handleContinue(e)}
                    className="text-left min-w-0"
                  >
                    <div className="font-serif text-[15px] text-[hsl(var(--foreground))] group-hover:text-[hsl(var(--accent-gold))] transition-colors truncate">
                      {e.title || "Untitled"}
                    </div>
                    <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] truncate">
                      {[e.category, e.destination_tag].filter(Boolean).join(" · ") || "Uncategorized"}
                      {lastActivity && <span className="ml-1.5">· {fmt.formatDate(lastActivity)}</span>}
                    </div>
                  </button>

                  <div className="hidden sm:flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em]">
                    <span
                      className={`inline-flex items-center gap-1 ${
                        e.status === "draft"
                          ? "text-[hsl(var(--foreground))]"
                          : "text-[hsl(var(--foreground-muted))]/60"
                      }`}
                    >
                      <CircleDot className="size-2.5" strokeWidth={e.status === "draft" ? 2.5 : 1.5} />
                      Draft
                    </span>
                    <span className="text-[hsl(var(--foreground-muted))]/30">·</span>
                    <span
                      className={`inline-flex items-center gap-1 ${
                        isPublished
                          ? "text-[hsl(var(--accent-sage))]"
                          : isScheduled
                          ? "text-[hsl(var(--accent-gold))]"
                          : "text-[hsl(var(--foreground-muted))]/40"
                      }`}
                    >
                      <CircleDot className="size-2.5" strokeWidth={isPublished || isScheduled ? 2.5 : 1.5} />
                      {isScheduled ? "Scheduled" : "Published"}
                    </span>
                    <span className="text-[hsl(var(--foreground-muted))]/30">·</span>
                    <span
                      className={`inline-flex items-center gap-1 ${
                        socialsCount > 0
                          ? "text-[hsl(var(--accent-gold))]"
                          : "text-[hsl(var(--foreground-muted))]/40"
                      }`}
                      data-testid={`content-dashboard-pipeline-socials-${e.id}`}
                    >
                      <CircleDot className="size-2.5" strokeWidth={socialsCount > 0 ? 2.5 : 1.5} />
                      {socialsCount === 0 ? "Not repurposed" : `${socialsCount} social${socialsCount === 1 ? "" : "s"}`}
                    </span>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => setRepurposeEntry(e)}
                      title="Repurpose for social"
                      data-testid={`content-dashboard-pipeline-repurpose-${e.id}`}
                      className={`size-8 inline-flex items-center justify-center rounded transition-colors ${
                        socialsCount > 0
                          ? "text-[hsl(var(--accent-gold))] hover:bg-[hsl(var(--accent-gold))]/10"
                          : "text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))] hover:bg-[hsl(var(--background-paper))]"
                      }`}
                    >
                      <Share2 className="size-3.5" strokeWidth={1.5} />
                    </button>
                    {isPublished && e.slug && (
                      <a
                        href={`/blog/${e.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        title="View on public site"
                        className="size-8 inline-flex items-center justify-center rounded text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))] hover:bg-[hsl(var(--background-paper))]"
                      >
                        <ExternalLink className="size-3.5" strokeWidth={1.5} />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* Recently published */}
      <Section
        title="Freshly on the site"
        hint="Recently published"
        action={
          <a
            href="/blog"
            target="_blank"
            rel="noreferrer"
            className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))] transition-colors inline-flex items-center gap-1"
            data-testid="content-dashboard-view-public-journal"
          >
            View The Journal <ExternalLink className="size-3" strokeWidth={1.5} />
          </a>
        }
      >
        {!buckets ? (
          <div className="space-y-2">
            {[0, 1].map((i) => (
              <Skeleton key={i} className="h-14" />
            ))}
          </div>
        ) : buckets.published.length === 0 ? (
          <Empty
            title="Nothing published yet."
            hint="The journal goes live the moment you publish your first piece."
            icon={<Share2 className="size-5" strokeWidth={1.25} />}
          />
        ) : (
          <div data-testid="content-dashboard-published">
            {buckets.published.map((e) => (
              <div
                key={e.id}
                className="group flex items-center justify-between gap-4 py-3 border-b border-[rgba(0,0,0,0.04)] last:border-0"
                data-testid={`content-dashboard-pub-${e.id}`}
              >
                <button
                  onClick={() => handleContinue(e)}
                  className="text-left min-w-0 flex-1"
                >
                  <div className="font-serif text-[15px] text-[hsl(var(--foreground))] group-hover:text-[hsl(var(--accent-gold))] transition-colors truncate">
                    {e.title || "Untitled"}
                  </div>
                  <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] truncate">
                    {[e.category, e.destination_tag].filter(Boolean).join(" · ") || "Uncategorized"}
                  </div>
                </button>
                <div className="shrink-0 flex items-center gap-3">
                  <span className="font-mono text-[11px] text-[hsl(var(--foreground-muted))]">
                    {fmt.formatDate(e.published_at)}
                  </span>
                  {e.slug && (
                    <a
                      href={`/blog/${e.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      title="View on public site"
                      className="size-8 inline-flex items-center justify-center rounded text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))] hover:bg-[hsl(var(--background-paper))]"
                      data-testid={`content-dashboard-pub-view-${e.id}`}
                    >
                      <ExternalLink className="size-3.5" strokeWidth={1.5} />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <RepurposeModal
        open={!!repurposeEntry}
        entry={repurposeEntry}
        onClose={() => setRepurposeEntry(null)}
        onSaved={(count) => {
          setRepurposeToast(`${count} social draft${count === 1 ? "" : "s"} saved.`);
          setRefreshKey((k) => k + 1);
          setTimeout(() => setRepurposeToast(null), 3500);
        }}
      />
      {repurposeToast && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-full bg-[hsl(var(--foreground))] text-[hsl(var(--primary-foreground))] shadow-2xl font-mono text-[11px] uppercase tracking-[0.18em]"
          data-testid="content-dashboard-repurpose-toast"
        >
          {repurposeToast}
        </div>
      )}
    </div>
  );
}
